import type { ApiQuestionSetting } from '../../../../internalTypes';

export type ChoiceRandomizationType = 'RANDOM_ORDER' | 'RANDOM_FLIP' | 'LIMITED_CHOICES';

export type ChoiceRandomizationConfig = {
  enabled: true;
  type: ChoiceRandomizationType;
  numberOfChoices?: number;
};

function shuffleArray<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function parseChoiceRandomization(
  configs: ApiQuestionSetting[]
): ChoiceRandomizationConfig | undefined {
  const choiceConfig = configs.find(config => config.key === 'CHOICE_RANDOMIZATION');
  if (choiceConfig?.value?.[0]?.toLowerCase() === 'true') {
    const rawType = choiceConfig.childConfigs
      ?.find(config => config.key === 'RANDOMIZATION_TYPE')
      ?.value?.[0]?.toUpperCase();
    const type: ChoiceRandomizationType =
      rawType === 'RANDOM_FLIP' || rawType === 'LIMITED_CHOICES' ? rawType : 'RANDOM_ORDER';
    const numberRaw = choiceConfig.childConfigs
      ?.find(config => config.key === 'NUMBER_OF_CHOICES')
      ?.value?.[0];
    const numberOfChoices = numberRaw ? Number(numberRaw) : undefined;

    return {
      enabled: true,
      type,
      ...(numberOfChoices !== undefined && Number.isFinite(numberOfChoices)
        ? { numberOfChoices }
        : {}),
    };
  }

  const shuffleOrder =
    configs.find(config => config.key === 'SHUFFLE_ORDER')?.value?.[0]?.toLowerCase() === 'true';
  if (shuffleOrder) {
    return { enabled: true, type: 'RANDOM_ORDER' };
  }

  return undefined;
}

export function applyChoiceRandomization<T extends { id: string }>(
  options: T[],
  config: ChoiceRandomizationConfig | undefined,
  pinnedOptionIds?: string[]
): { options: T[]; shuffleOptions: boolean } {
  if (!config?.enabled || options.length <= 1) {
    return { options, shuffleOptions: false };
  }

  switch (config.type) {
    case 'RANDOM_FLIP': {
      const flipped = Math.random() < 0.5 ? [...options].reverse() : options;
      return { options: flipped, shuffleOptions: true };
    }
    case 'LIMITED_CHOICES': {
      const requestedCount = config.numberOfChoices ?? options.length;
      const count = Math.min(Math.max(1, requestedCount), options.length);
      if (count >= options.length) {
        return { options: shuffleArray(options), shuffleOptions: true };
      }

      const pinnedSet = new Set(
        (pinnedOptionIds ?? []).filter(id => options.some(option => option.id === id))
      );
      const pinned = options.filter(option => pinnedSet.has(option.id));
      const remaining = options.filter(option => !pinnedSet.has(option.id));
      const slotsLeft = Math.max(0, count - pinned.length);
      const selectedRemaining = shuffleArray(remaining).slice(0, slotsLeft);

      return {
        options: shuffleArray([...pinned, ...selectedRemaining]),
        shuffleOptions: true,
      };
    }
    case 'RANDOM_ORDER':
    default:
      return { options: shuffleArray(options), shuffleOptions: true };
  }
}
